import { useState } from 'react'
import { Box, Button, Checkbox, Dialog, Portal, Stack, Text } from '@chakra-ui/react'
import { LuShieldEllipsis } from 'react-icons/lu'
import { showSuccess } from '@/lib/alert'
import { useUpdateMemberPermissions } from '@/hooks/agendas/useUpdateMemberPermissions'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import type { AgendaMember, AgendaPermission } from '@/services/agendas'

type PermissionGroup = {
  label: string
  permissions: { value: AgendaPermission; label: string }[]
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'Permissões',
    permissions: [
      { value: 'CanInviteMembers', label: 'Convidar membros' },
      { value: 'CanRemoveMembers', label: 'Remover membros' },
      { value: 'CanManagePermissions', label: 'Gerenciar permissões' },
    ],
  },
  {
    label: 'Evento',
    permissions: [
      { value: 'CanViewEvents', label: 'Visualizar eventos' },
      { value: 'CanEditEvents', label: 'Editar eventos' },
    ],
  },
  {
    label: 'Notas Internas',
    permissions: [
      { value: 'CanViewNotes', label: 'Visualizar notas internas' },
      { value: 'CanEditNotes', label: 'Editar notas internas' },
    ],
  },
  {
    label: 'Financeiro',
    permissions: [
      { value: 'CanViewFinancial', label: 'Visualizar financeiro' },
      { value: 'CanEditFinancial', label: 'Editar financeiro' },
    ],
  },
]

type Props = {
  agendaId: string
  member: AgendaMember
}

const EditMemberPermissionsButton = ({ agendaId, member }: Props) => {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<AgendaPermission[]>([])
  const { mutate: updatePermissions, isPending } = useUpdateMemberPermissions(agendaId)

  const handleOpen = () => {
    setSelected(member.permissions ?? [])
    setOpen(true)
  }

  const handleToggle = (permission: AgendaPermission) => {
    setSelected((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission],
    )
  }

  const handleSave = () => {
    updatePermissions(
      { targetUserId: member.user.id, permissions: selected },
      {
        onSuccess: () => {
          showSuccess('Permissões atualizadas com sucesso!')
          setOpen(false)
        },
      },
    )
  }

  return (
    <>
      <Button colorPalette="primary" variant="outline" size="xs" onClick={handleOpen}>
        <LuShieldEllipsis />
        Alterar permissões
      </Button>

      {open && (
        <Dialog.Root
          size="sm"
          placement="center"
          open={open}
          onOpenChange={({ open }) => setOpen(open)}
        >
          <Portal>
            <CustomDialogBackdrop />
            <Dialog.Positioner>
              <Dialog.Content m={6}>
                <Dialog.Header>
                  <Dialog.Title>Alterar permissões</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Text mb={4} color="fg.muted" fontSize="sm">
                    Selecione as permissões para <strong>{member.user.name}</strong>:
                  </Text>
                  <Stack gap={5}>
                    {PERMISSION_GROUPS.map((group) => (
                      <Box key={group.label}>
                        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="fg.muted" mb={2}>
                          {group.label}
                        </Text>
                        <Stack gap={2}>
                          {group.permissions.map(({ value, label }) => (
                            <Checkbox.Root
                              key={value}
                              checked={selected.includes(value)}
                              onCheckedChange={() => handleToggle(value)}
                              colorPalette="primary"
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label fontSize="sm">{label}</Checkbox.Label>
                            </Checkbox.Root>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Dialog.Body>
                <Dialog.Footer>
                  <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                    Cancelar
                  </Button>
                  <Button size="sm" colorPalette="primary" onClick={handleSave} loading={isPending}>
                    Salvar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      )}
    </>
  )
}

export default EditMemberPermissionsButton
